import { Formatter } from "utils/formatter";
import { ApiService as ApiServiceServerSide } from "services/serverInClient";
import { formatData } from "utils/functions";
import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import { useForm, FormProvider } from "react-hook-form";
import { toast } from "react-toastify";
import Toast from "components/Toast/Toast";
import { useLocale } from "hooks";
import translation from "views/News/NewsList/translation";
import NewsListComponent from "views/News/NewsList/components/NewsListComponent";
import { ITEMS_PER_PAGE, TABS } from "views/News/NewsList/components/constants";
import { CMS_ENVIRONMENT, CONTENT_TYPES, ENTITY_ID } from "utils/constants";
import type { ReactPaginateProps } from "react-paginate";
import type { GetContentParams } from "types";

function NewsList({ tagTreeId }) {
  const { locale } = useLocale();
  const [activeTab, setActiveTab] = useState<string>("news"); // news | events
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(true);
  const [listData, setListData] = useState<Array<any>>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const methods = useForm();
  const fetchController = useRef<number>(0);
  const [totalPagesCount, setTotalPagesCount] = useState<number>(0);
  const [newsAndeventsTagTrees, setNewsAndEventsTagTrees] = useState([]);
  const [eventsTagTrees, setEventsTagTrees] = useState([]);
  const [newsTagTrees, setNewsTagTrees] = useState([]);

  useEffect(() => {
    const fetchTagTrees = async () => {
      if (router.locale && tagTreeId) {
        const apiService = new ApiServiceServerSide(locale);
        const firstDropdownOptions = await apiService.getTagTrees(
          ENTITY_ID[locale.toLocaleUpperCase()][CMS_ENVIRONMENT]?.MENUS,
          tagTreeId,
          false
        );

        const [alumniNewsTagTrees, alumniEventsTagTrees] = await Promise.all(
          firstDropdownOptions?.result?.map((item) =>
            apiService.getTagTrees(
              ENTITY_ID[locale.toLocaleUpperCase()][CMS_ENVIRONMENT]?.MENUS,
              item.id,
              false
            )
          ) || []
        );

        const generateOption = (item) => ({
          label: item.name?.split("-")?.[0],
          value: item.name,
        });
        const firstDropdownOptionsCleaned = firstDropdownOptions?.result
          ?.sort?.((a, b) => a?.siblingOrder - b.siblingOrder)
          ?.map(generateOption);

        setNewsAndEventsTagTrees(firstDropdownOptionsCleaned);

        setNewsTagTrees(alumniNewsTagTrees?.result?.map?.(generateOption));
        setEventsTagTrees(alumniEventsTagTrees?.result?.map?.(generateOption));
        goTab(firstDropdownOptionsCleaned?.[0]?.value);
      }
    };
    fetchTagTrees();
  }, [router.locale, tagTreeId]);

  useEffect(() => {
    if (methods.watch("category")) {
      handleSearch();
    }
  }, [methods.watch("category")]);

  const goTab = async (tabKey: string) => {
    const apiService = new ApiServiceServerSide(router.locale);
    fetchController.current++;
    const innerController = fetchController.current;
    setActiveTab(tabKey);
    methods.reset({ search: "", category: "" });

    try {
      setLoading(true);
      const res = await apiService.getContent(
        {
          tagTrees: [tabKey || "اخبار"],
          size: ITEMS_PER_PAGE,
          contentTypeUniqueId: CONTENT_TYPES.NEWS,
          orderTagTree: "child",
          offset: 0,
          orderBy: [
            tabKey === TABS[1]
              ? "content.due:desc"
              : "content.publish_date:desc",
          ],
        },
        { formatter: false }
      );
      if (innerController === fetchController.current) {
        setListData(Formatter.formatData({ data: res }));
        setCurrentPage(1);
        setTotalPagesCount(Math.ceil((res?.count ?? 0) / ITEMS_PER_PAGE));
      }
    } catch (error) {
      toast.error(translation[locale]?.FAILED);
    } finally {
      if (innerController === fetchController.current) {
        setLoading(false);
      }
    }
  };

  const handleSearch = async (isEmptyInput = false) => {
    const apiService = new ApiServiceServerSide(router.locale);
    const filterObj = {
      search: methods.watch("search"),
      category: methods.watch("category")?.value || "",
    };

    if (isEmptyInput && !Boolean(filterObj.search)) {
      return void toast.error(translation[locale]?.FILL_ERROR_MESSAGE);
    }

    const tagTrees = [filterObj?.category ? filterObj.category : activeTab];

    try {
      setLoading(true);
      const queries = {
        search: filterObj?.search,
        category: filterObj?.category || "",
        page: 1,
      };
      if (isEmptyInput) {
        delete queries.search;
      }
      const params: GetContentParams = {
        tagTrees,
        size: ITEMS_PER_PAGE,
        contentTypeUniqueId: CONTENT_TYPES.NEWS,
        orderTagTree: "child",
        orderBy: [
          router.query?.tab === TABS[1]
            ? "content.due:desc"
            : "content.publish_date:desc",
        ],
      };

      if (!isEmptyInput && filterObj.search) {
        params.fieldCode = ["title"];
        params.fieldValue = [queries.search];
      }

      const res = await apiService.getContent(params, { formatter: false });
      setListData(formatData({ data: res }));
      setTotalPagesCount(Math.ceil((res?.count || 0) / ITEMS_PER_PAGE));
      setCurrentPage(1);
    } catch (e) {
      toast.error(translation[locale]?.FAILED);
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const onPageChange: ReactPaginateProps["onPageChange"] = async ({
    selected,
  }) => {
    const apiService = new ApiServiceServerSide(router.locale);
    const filterObj = {
      search: methods.watch("search"),
      category: methods.watch("category")?.value || "",
    };

    const tagTrees = [filterObj?.category ? filterObj.category : activeTab];

    try {
      setLoading(true);
      const params: GetContentParams = {
        tagTrees,
        size: ITEMS_PER_PAGE,
        offset: selected * ITEMS_PER_PAGE,
        contentTypeUniqueId: CONTENT_TYPES.NEWS,
        orderTagTree: "child",
        orderBy: [
          router.query?.tab === TABS[1]
            ? "content.due:desc"
            : "content.publish_date:desc",
        ],
      };
      const res = await apiService.getContent(params, { formatter: false });
      setListData(Formatter.formatData({ data: res }));
      setTotalPagesCount(Math.ceil((res?.count || 0) / ITEMS_PER_PAGE));
      setCurrentPage(selected + 1);
    } catch (e) {
      toast.error(translation[locale]?.FAILED);
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <Toast />
      <NewsListComponent
        data={{
          cmsCount: totalPagesCount,
          cmsData: listData,
          page: currentPage,
          eventsTagTrees,
          newsTagTrees,
        }}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        totalPagesCount={totalPagesCount}
        setTotalPagesCount={setTotalPagesCount}
        onPageChange={onPageChange}
        handleSearch={handleSearch}
        goTab={goTab}
        loading={loading}
        currentPage={currentPage}
        listData={listData}
        layout="secondary"
        tabOptions={newsAndeventsTagTrees}
      />
    </FormProvider>
  );
}

export default NewsList;
